"use client"

import React, { Component } from "react"
import { Editor } from "@toast-ui/react-editor"
import "@toast-ui/editor/dist/toastui-editor.css"
import "../../../resources/scss/weekly-write.scss"
import { connect } from "react-redux"
import actionTypes from "../../../constants/actionTypes"
import DOMPurify from "dompurify"

class WeeklyWrite extends Component {
    constructor(props) {
        super(props)
        this.state = {
            title: "",
            publishDate: "",
            content: "",
            frontImage: null,
            backImage: null,
            frontImagePreview: null,
            backImagePreview: null,
            isPreviewOpen: false,
            isSaving: false,
            errors: {},
            hasPermission: false, // 권한 상태 추가
        }
        this.editorRef = React.createRef()
        this.frontImageInputRef = React.createRef()
        this.backImageInputRef = React.createRef()
    }

    componentDidMount() {
        // 권한 체크
        if (!this.hasAdminPermission()) {
            alert("주보 작성 권한이 없습니다.")
            window.location.href = "/weekly"
            return
        }

        this.setState({ hasPermission: true })

        // 현재 날짜를 기본값으로 설정
        const today = new Date().toISOString().split("T")[0]
        this.setState({
            publishDate: today,
        })
    }

    // 관리자 권한 체크 함수
    hasAdminPermission = () => {
        const { login } = this.props
        const { isLogin, role } = login || {}


        if (!isLogin || !role) {
            return false
        }

        const adminRoles = ["ROLE_WEB_ADMIN", "ROLE_ADMIN"]
        const hasPermission = adminRoles.includes(role)

        return hasPermission
    }

    componentDidUpdate(prevProps) {
        const { board } = this.props
        const writeState = board?.write || {}
        const prevWriteState = prevProps.board?.write || {}

        // 작성 성공 시 처리
        if (!writeState.loading && writeState.success && !prevWriteState.success) {
            alert("주보가 발행되었습니다.")

            // 작성 상태 초기화
            this.props.dispatch({
                type: actionTypes.BOARD_WRITE_RESET,
            })

            // 주보 목록 데이터 새로고침
            this.props.dispatch({
                type: actionTypes.BOARD_FETCH,
                payload: {
                    menuId: 2,
                    page: 0,
                    size: 10,
                },
            })

            // 주보 목록 페이지로 이동
            setTimeout(() => {
                window.location.href = "/weekly"
            }, 1000)
        }

        // 작성 실패 시 처리
        if (!writeState.loading && writeState.error && !prevWriteState.error) {
            console.error("주보 작성 실패 감지:", writeState.error)
            alert(`저장 실패: ${writeState.error}`)
            this.setState({ isSaving: false })
        }

        // 로딩 상태 변화 감지
        if (writeState.loading !== prevWriteState.loading) {
            this.setState({ isSaving: writeState.loading })
        }
    }

    // 로그인 정보 가져오기 함수 (숫자 ID 반환)
    getLoginInfo = () => {

        const { login, user, config } = this.props
        let userId = null
        let userInfo = null

        // 1. login 상태에서 확인 (가장 일반적)
        if (login && login.isLogin && login.id) {
            userId = login.id
            userInfo = {
                id: login.id,
                name: login.name,
                isLogin: login.isLogin,
                role: login.role,
            }
        }
        // 2. user 상태에서 확인
        else if (user?.userInfo?.id) {
            userId = user.userInfo.id
            userInfo = user.userInfo
        }
        // 3. user.loginInfo에서 확인
        else if (user?.loginInfo?.id) {
            userId = user.loginInfo.id
            userInfo = user.loginInfo
        }
        // 4. config 상태에서 확인
        else if (config?.isLogin && config?.loginInfo?.id) {
            userId = config.loginInfo.id
            userInfo = config.loginInfo
        }
        // 5. localStorage에서 확인
        else {
            const storedUserId = localStorage.getItem("userId")
            const storedUserInfo = localStorage.getItem("userInfo")

            if (storedUserId) {
                userId = storedUserId
                try {
                    userInfo = storedUserInfo ? JSON.parse(storedUserInfo) : { id: storedUserId }
                } catch (e) {
                    userInfo = { id: storedUserId }
                }
            }
        }

        // 숫자 ID로 변환 및 검증
        let numericUserId = null
        if (userId) {
            // 문자열을 숫자로 변환
            const parsed = Number.parseInt(userId, 10)
            if (!isNaN(parsed) && parsed > 0) {
                numericUserId = parsed
            } else {
                console.warn("⚠️ 유효하지 않은 사용자 ID:", userId)
            }
        }

        // 기본값 설정 (서버에서 Long 타입을 요구하므로 숫자 1 사용)
        const finalUserId = numericUserId || 1

        return {
            userId: finalUserId,
            userInfo: userInfo,
            isLoggedIn: !!numericUserId,
            originalUserId: userId,
        }
    }

    handleInputChange = (field, value) => {
        this.setState({
            [field]: value,
            errors: {
                ...this.state.errors,
                [field]: "",
            },
        })
    }

    handleEditorChange = () => {
        if (this.editorRef.current) {
            const content = this.editorRef.current.getInstance().getHTML()
            this.setState({ content })
        }
    }

    handleImageUpload = (type, event) => {
        const file = event.target.files[0]
        if (!file) return

        // 이미지 파일인지 확인
        if (!file.type.match("image.*")) {
            alert("이미지 파일만 업로드 가능합니다.")
            return
        }

        // 파일 크기 제한 (10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert("파일 크기는 10MB를 초과할 수 없습니다.")
            return
        }

        // 이미지 미리보기 생성
        const reader = new FileReader()
        reader.onload = (e) => {
            if (type === "front") {
                this.setState({
                    frontImage: file,
                    frontImagePreview: e.target.result,
                    errors: {
                        ...this.state.errors,
                        frontImage: "",
                    },
                })
            } else {
                this.setState({
                    backImage: file,
                    backImagePreview: e.target.result,
                    errors: {
                        ...this.state.errors,
                        backImage: "",
                    },
                })
            }
        }
        reader.readAsDataURL(file)
    }

    removeImage = (type) => {
        if (type === "front") {
            this.setState({
                frontImage: null,
                frontImagePreview: null,
            })
            if (this.frontImageInputRef.current) {
                this.frontImageInputRef.current.value = ""
            }
        } else {
            this.setState({
                backImage: null,
                backImagePreview: null,
            })
            if (this.backImageInputRef.current) {
                this.backImageInputRef.current.value = ""
            }
        }
    }

    // 이미지 업로드 함수 (임시로 base64 사용)
    uploadImage = async (imageFile) => {
        try {
            return new Promise((resolve) => {
                const reader = new FileReader()
                reader.onload = (e) => {
                    resolve(e.target.result)
                }
                reader.readAsDataURL(imageFile)
            })
        } catch (error) {
            console.error("이미지 업로드 실패:", error)
            throw error
        }
    }

    validateForm = () => {
        const errors = {}
        const { title, publishDate, frontImage } = this.state

        if (!title.trim()) {
            errors.title = "주보 제목을 입력해주세요."
        }

        if (title.length > 14) {
            errors.title = "제목은 15자 이내로 작성해주세요."
        }

        if (!publishDate) {
            errors.publishDate = "발행 날짜를 선택해주세요."
        }

        if (!frontImage) {
            errors.frontImage = "주보 앞면 이미지를 업로드해주세요."
        }

        this.setState({ errors })
        return Object.keys(errors).length === 0
    }

    handleSave = async (isDraft = false) => {
        // 권한 재확인
        if (!this.hasAdminPermission()) {
            alert("주보 작성 권한이 없습니다.")
            return
        }

        if (!this.validateForm()) {
            return
        }

        try {
            // 로그인 정보 가져오기
            const { userId, userInfo, isLoggedIn, originalUserId } = this.getLoginInfo()


            // 기본 내용 설정
            let contentsHtml = "<div><p>주보 내용</p></div>"

            // 앞면 이미지 처리
            if (this.state.frontImage) {
                const frontImageUrl = await this.uploadImage(this.state.frontImage)
                contentsHtml = `<div><p>주보 앞면</p><img src="${frontImageUrl}" alt="주보 앞면" /></div>`
            }

            // 뒷면 이미지 처리 (있는 경우)
            if (this.state.backImage) {
                const backImageUrl = await this.uploadImage(this.state.backImage)
                contentsHtml += `<div><p>주보 뒷면</p><img src="${backImageUrl}" alt="주보 뒷면" /></div>`
            }

            // 추가 내용이 있는 경우 HTML에 추가 (Sanitize 적용)
            if (this.state.content && this.state.content !== "<p><br></p>") {
                const sanitizedContent = DOMPurify.sanitize(this.state.content, {
                    ALLOWED_TAGS: [
                        "p",
                        "br",
                        "strong",
                        "b",
                        "em",
                        "i",
                        "u",
                        "h1",
                        "h2",
                        "h3",
                        "h4",
                        "h5",
                        "h6",
                        "ul",
                        "ol",
                        "li",
                        "blockquote",
                    ],
                    ALLOWED_ATTR: [],
                    ALLOW_DATA_ATTR: false,
                    FORBID_SCRIPT: true,
                    FORBID_TAGS: ["script", "object", "embed", "form", "input", "button", "a", "img"],
                    FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover", "onfocus", "onblur", "onchange", "onsubmit"],
                })
                contentsHtml += `<div>${sanitizedContent}</div>`
            }

            // API 요청 데이터 구성 (createId는 반드시 숫자)
            const requestData = {
                boardId: 2, // 주보 게시판 ID
                title: this.state.title,
                contents: contentsHtml,
                createId: userId, // 숫자 ID 사용
            }


            // 데이터 타입 검증
            if (typeof requestData.createId !== "number" || requestData.createId <= 0) {
                console.error("❌ createId가 유효한 숫자가 아닙니다:", requestData.createId)
                alert("사용자 정보가 올바르지 않습니다. 다시 로그인해주세요.")
                return
            }

            // Redux saga를 통한 API 호출
            if (this.props.dispatch && actionTypes.BOARD_WRITE) {

                this.setState({ isSaving: true })

                this.props.dispatch({
                    type: actionTypes.BOARD_WRITE,
                    payload: requestData,
                })

            } else {
                console.error("dispatch 함수 또는 actionTypes.BOARD_WRITE가 없습니다.")
                alert("저장 기능을 사용할 수 없습니다.")
            }
        } catch (error) {
            console.error("주보 저장 준비 실패:", error)
            alert(`저장 준비 실패: ${error.message}`)
            this.setState({ isSaving: false })
        }
    }

    handlePreview = () => {
        if (this.editorRef.current) {
            const content = this.editorRef.current.getInstance().getHTML()
            this.setState({ content, isPreviewOpen: true })
        } else {
            this.setState({ isPreviewOpen: true })
        }
    }

    formatDate = (dateString) => {
        if (!dateString) return ""
        const date = new Date(dateString)
        return date.toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    render() {
        const {
            title,
            publishDate,
            content,
            frontImagePreview,
            backImagePreview,
            isPreviewOpen,
            isSaving,
            errors,
            hasPermission,
        } = this.state

        const { board } = this.props
        const writeState = board?.write || {}
        const { userId, userInfo, isLoggedIn, originalUserId } = this.getLoginInfo()

        // 권한이 없으면 로딩 화면 표시
        if (!hasPermission) {
            return (
                <div className="weekly-write-page">
                    <div className="container">
                        <div className="permission-check">
                            <h2>권한 확인 중...</h2>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div className="weekly-write-page">
                <div className="container">
                    {/* 헤더 */}
                    <div className="write-header">
                        <h1>
                            <span className="icon">📖</span>
                            주보 작성
                        </h1>
                        <p className="subtitle">새로운 주보를 작성하고 발행하세요</p>
                    </div>

                    {/* 디버그 정보 */}
                    {process.env.NODE_ENV === "development" && (
                        <div style={{ background: "#f0f0f0", padding: "10px", margin: "10px 0", fontSize: "12px" }}>
                            <strong>디버그 정보:</strong>
                            <br />- isSaving: {isSaving.toString()}
                            <br />- writeState.loading: {writeState.loading?.toString()}
                            <br />- writeState.success: {writeState.success?.toString()}
                            <br />- writeState.error: {writeState.error || "null"}
                            <br />- actionTypes.BOARD_WRITE: {actionTypes.BOARD_WRITE}
                            <br />- 로그인 여부: {isLoggedIn.toString()}
                            <br />- 원본 사용자 ID: {originalUserId}
                            <br />- 숫자 사용자 ID: {userId} (타입: {typeof userId})
                            <br />- 사용자 정보: {userInfo ? JSON.stringify(userInfo) : "null"}
                            <br />- localStorage 토큰: {localStorage.getItem("token") ? "있음" : "없음"}
                            <br />- 권한: {this.hasAdminPermission().toString()}
                            <br />
                        </div>
                    )}

                    {/* 작성 폼 */}
                    <div className="write-form">
                        {/* 기본 정보 */}
                        <div className="form-section">
                            <h3 className="section-title">
                                <span className="icon">📝</span>
                                기본 정보
                            </h3>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="label">주보 제목 * (15자 이내)</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.title ? "error" : ""}`}
                                        value={title}
                                        onChange={(e) => this.handleInputChange("title", e.target.value)}
                                        placeholder="예: 2025년 1월 첫째 주"
                                        maxLength={15}
                                    />
                                    {errors.title && <div className="error-message">{errors.title}</div>}
                                    <div className="character-count">{title.length}/15</div>
                                </div>

                                <div className="form-group">
                                    <label className="label">발행 날짜 *</label>
                                    <input
                                        type="date"
                                        className={`input ${errors.publishDate ? "error" : ""}`}
                                        value={publishDate}
                                        onChange={(e) => this.handleInputChange("publishDate", e.target.value)}
                                    />
                                    {errors.publishDate && <div className="error-message">{errors.publishDate}</div>}
                                </div>
                            </div>
                        </div>

                        {/* 주보 이미지 업로드 */}
                        <div className="form-section">
                            <h3 className="section-title">
                                <span className="icon">🖼️</span>
                                주보 이미지
                            </h3>

                            <div className="form-row">
                                {/* 앞면 이미지 */}
                                <div className="form-group">
                                    <label className="label">주보 앞면 이미지 *</label>
                                    <div className={`image-upload ${errors.frontImage ? "error" : ""}`}>
                                        {frontImagePreview ? (
                                            <div className="image-preview-container">
                                                <img src={frontImagePreview || "/placeholder.svg"} alt="주보 앞면" className="image-preview" />
                                                <div className="image-actions">
                                                    <button
                                                        type="button"
                                                        className="image-action-btn change"
                                                        onClick={() => this.frontImageInputRef.current?.click()}
                                                    >
                                                        변경
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="image-action-btn remove"
                                                        onClick={() => this.removeImage("front")}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="upload-placeholder" onClick={() => this.frontImageInputRef.current?.click()}>
                                                <div className="upload-icon">➕</div>
                                                <div className="upload-text">주보 앞면 이미지 업로드</div>
                                                <div className="upload-hint">클릭하여 이미지 선택</div>
                                            </div>
                                        )}
                                        <input
                                            ref={this.frontImageInputRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={(e) => this.handleImageUpload("front", e)}
                                        />
                                    </div>
                                    {errors.frontImage && <div className="error-message">{errors.frontImage}</div>}
                                </div>

                                {/* 뒷면 이미지 */}
                                <div className="form-group">
                                    <label className="label">주보 뒷면 이미지 (선택사항)</label>
                                    <div className="image-upload">
                                        {backImagePreview ? (
                                            <div className="image-preview-container">
                                                <img src={backImagePreview || "/placeholder.svg"} alt="주보 뒷면" className="image-preview" />
                                                <div className="image-actions">
                                                    <button
                                                        type="button"
                                                        className="image-action-btn change"
                                                        onClick={() => this.backImageInputRef.current?.click()}
                                                    >
                                                        변경
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="image-action-btn remove"
                                                        onClick={() => this.removeImage("back")}
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="upload-placeholder" onClick={() => this.backImageInputRef.current?.click()}>
                                                <div className="upload-icon">➕</div>
                                                <div className="upload-text">주보 뒷면 이미지 업로드</div>
                                                <div className="upload-hint">클릭하여 이미지 선택</div>
                                            </div>
                                        )}
                                        <input
                                            ref={this.backImageInputRef}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={(e) => this.handleImageUpload("back", e)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 주보 내용 */}
                        <div className="form-section">
                            <h3 className="section-title">
                                <span className="icon">✍️</span>
                                주보 내용 (선택사항)
                            </h3>

                            <div className="editor-container">
                                <Editor
                                    ref={this.editorRef}
                                    initialValue=""
                                    previewStyle="vertical"
                                    height="400px"
                                    initialEditType="wysiwyg"
                                    useCommandShortcut={true}
                                    onChange={this.handleEditorChange}
                                    toolbarItems={[
                                        ["heading", "bold", "italic", "strike"],
                                        ["hr", "quote"],
                                        ["ul", "ol", "task", "indent", "outdent"],
                                        ["table", "image", "link"],
                                        ["code", "codeblock"],
                                        ["scrollSync"],
                                    ]}
                                />
                            </div>
                            <p className="form-hint">주보 이미지 외에 추가로 표시할 내용이 있으면 입력하세요.</p>
                        </div>

                        {/* 액션 버튼 */}
                        <div className="action-buttons">
                            <button className="btn secondary" onClick={this.handlePreview} disabled={isSaving}>
                                <span className="icon">👁️</span>
                                미리보기
                            </button>

                            <button className="btn secondary" onClick={() => this.handleSave(true)} disabled={isSaving}>
                                <span className="icon">💾</span>
                                {isSaving ? "저장 중..." : "임시저장"}
                            </button>

                            <button className="btn primary" onClick={() => this.handleSave(false)} disabled={isSaving}>
                                <span className="icon">🚀</span>
                                {isSaving ? "발행 중..." : "발행하기"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* 미리보기 모달 */}
                {isPreviewOpen && (
                    <div className="preview-modal" onClick={() => this.setState({ isPreviewOpen: false })}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>주보 미리보기</h3>
                                <button className="close-btn" onClick={() => this.setState({ isPreviewOpen: false })}>
                                    ×
                                </button>
                            </div>
                            <div className="modal-body">
                                {title && <h2 className="preview-title">{title}</h2>}

                                {publishDate && (
                                    <p className="preview-date">
                                        <strong>발행일:</strong> {this.formatDate(publishDate)}
                                    </p>
                                )}

                                <div className="preview-images">
                                    {frontImagePreview && (
                                        <div className="preview-image-container">
                                            <h4>주보 앞면</h4>
                                            <img src={frontImagePreview || "/placeholder.svg"} alt="주보 앞면" className="preview-image" />
                                        </div>
                                    )}

                                    {backImagePreview && (
                                        <div className="preview-image-container">
                                            <h4>주보 뒷면</h4>
                                            <img src={backImagePreview || "/placeholder.svg"} alt="주보 뒷면" className="preview-image" />
                                        </div>
                                    )}
                                </div>

                                {content && content !== "<p><br></p>" && (
                                    <>
                                        <h4>추가 내용</h4>
                                        <div dangerouslySetInnerHTML={{ __html: content }} />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

const mapStateToProps = (state) => {

    return {
        login: state.login,
        user: state.user,
        config: state.config,
        board: state.board,
    }
}

export default connect(mapStateToProps)(WeeklyWrite)
